import { ExtendedCommunication } from '@seen/core/models/communications/ExtendedCommunication'
import type { FaxErrorInboxItem } from '@seen/core/models/inbox'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { Participant } from '@seen/core/models/patients/participant'
import { ExtendedPractitioner } from '@seen/core/models/practitioner'
import { isOfType } from '@seen/core/utils'
import { IconAlertTriangle } from '@tabler/icons-react'
import type { ReactNode } from 'react'
import { ParticipantTaskRowBadge } from '#src/components/ParticipantTaskRowBadge.tsx'
import type { NotificationItem } from '#src/hooks/useNotificationItems.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { InboxRow } from '#src/pages/inbox/row/InboxRow.tsx'
import { InboxRowContent } from '#src/pages/inbox/row/InboxRowContent.tsx'
import { InboxRowSubtext } from '#src/pages/inbox/row/InboxRowSubtext.tsx'
import { InboxRowText } from '#src/pages/inbox/row/InboxRowText.tsx'

interface FaxErrorInboxItemRowProps {
  item: FaxErrorInboxItem
  notificationItem: NotificationItem
  onSelect?: () => void
  isSelected?: boolean
  sidebarOpen?: boolean
}

export function FaxErrorInboxItemRow({
  item,
  notificationItem,
  onSelect,
  isSelected,
  sidebarOpen,
}: FaxErrorInboxItemRowProps): ReactNode {
  const { data: communication } = useRead(
    ExtendedCommunication,
    item.communication
  )
  const recipientPractitionerRef = isOfType(item.recipient, 'Practitioner')
    ? item.recipient
    : undefined
  const recipientOrgRef = isOfType(item.recipient, 'Organization')
    ? item.recipient
    : undefined
  const { data: recipientPractitioner } = useRead(
    ExtendedPractitioner,
    recipientPractitionerRef
  )
  const { data: recipientOrg } = useRead(ExtendedOrganization, recipientOrgRef)
  const recipientName = recipientPractitioner?.name ?? recipientOrg?.name

  const { data: participant } = useRead(Participant, communication?.subject)

  return (
    <InboxRow
      to={item.actionUrl}
      notificationItem={notificationItem}
      onSelect={onSelect}
      isSelected={isSelected}
      sidebarOpen={sidebarOpen}
    >
      <IconAlertTriangle className="size-6 text-destructive" />
      <InboxRowContent>
        <InboxRowText
          notificationItem={notificationItem}
          badges={
            participant && (
              <ParticipantTaskRowBadge participant={participant.reference} />
            )
          }
        >
          {recipientName ? (
            <>Fax failed to send to {recipientName}</>
          ) : (
            <>Fax failed to send</>
          )}
        </InboxRowText>
        <InboxRowSubtext>{item.statusReason}</InboxRowSubtext>
      </InboxRowContent>
    </InboxRow>
  )
}
